import type { ClassSearchDetails } from '~entities/classes/modal/class-search';
import { formatTrialTimeRange } from '~shared/helpers/format-trial-datetime';

export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const normalizeClassDay = (day?: string | null) => {
  const trimmed = day?.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  const dayMap: Record<string, string> = {
    sun: 'Sunday',
    sunday: 'Sunday',
    mon: 'Monday',
    monday: 'Monday',
    tue: 'Tuesday',
    tues: 'Tuesday',
    tuesday: 'Tuesday',
    wed: 'Wednesday',
    weds: 'Wednesday',
    wednesday: 'Wednesday',
    thu: 'Thursday',
    thur: 'Thursday',
    thurs: 'Thursday',
    thursday: 'Thursday',
    fri: 'Friday',
    friday: 'Friday',
    sat: 'Saturday',
    saturday: 'Saturday',
  };

  return dayMap[normalized] ?? null;
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  if (
    typeof error === 'object' &&
    error &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: unknown } } }).response
      ?.data?.message === 'string'
  ) {
    return (error as { response: { data: { message: string } } }).response.data
      .message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const getAllowedClassDays = (
  offering: ClassSearchDetails | null | undefined,
): Array<string> => {
  const availabilityDays = Array.isArray(offering?.availability)
    ? offering.availability
        .map((slot) => normalizeClassDay(slot.day))
        .filter((value): value is string => !!value)
    : [];

  const configuredDays =
    availabilityDays.length > 0
      ? availabilityDays
      : Array.isArray(offering?.days)
        ? offering.days
            .map((day) => normalizeClassDay(day))
            .filter((value): value is string => !!value)
        : [];

  return Array.from(new Set(configuredDays));
};

export const parseOfferingDate = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const datePart = value.split('T')[0];
  if (!datePart) return undefined;
  const parsed = new Date(`${datePart}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const toDateKey = (date: Date): string =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
    .toISOString()
    .slice(0, 10);

export const startOfLocalDay = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export const getAllowedDayIndexes = (
  offering: ClassSearchDetails | null | undefined,
): Set<number> =>
  new Set(
    getAllowedClassDays(offering)
      .map((day) =>
        WEEKDAY_LABELS.indexOf(day as (typeof WEEKDAY_LABELS)[number]),
      )
      .filter((index) => index >= 0),
  );

export const isBookablePrivateLessonDate = (
  date: Date,
  offering: ClassSearchDetails | null | undefined,
  allowedDayIndexes: Set<number>,
  windowStart?: Date,
  windowEnd?: Date,
): boolean => {
  if (!offering) return false;

  const current = startOfLocalDay(date);
  const today = startOfLocalDay(new Date());
  if (current < today) return false;

  const effectiveWindowStart =
    windowStart ?? parseOfferingDate(offering.startDate);
  const effectiveWindowEnd = windowEnd ?? parseOfferingDate(offering.endDate);
  if (effectiveWindowStart && current < startOfLocalDay(effectiveWindowStart)) {
    return false;
  }
  if (effectiveWindowEnd && current > startOfLocalDay(effectiveWindowEnd)) {
    return false;
  }

  if (allowedDayIndexes.size === 0) return true;
  return allowedDayIndexes.has(date.getDay());
};

export const findFirstBookableDate = (
  offering: ClassSearchDetails | null | undefined,
  allowedDayIndexes: Set<number>,
): string => {
  const windowStart = parseOfferingDate(offering?.startDate);
  const windowEnd = parseOfferingDate(offering?.endDate);
  const start = startOfLocalDay(new Date());
  for (let offset = 0; offset < 370; offset += 1) {
    const candidate = new Date(start);
    candidate.setDate(start.getDate() + offset);
    if (
      isBookablePrivateLessonDate(
        candidate,
        offering,
        allowedDayIndexes,
        windowStart,
        windowEnd,
      )
    ) {
      return toDateKey(candidate);
    }
  }
  return toDateKey(start);
};

export const getPrivateLessonDisplayTimezone = (
  booking?: { timezone?: string | null } | null,
  listTimezone?: string | null,
  studioTimezone?: string | null,
): string | undefined => {
  const storedTimezone = booking?.timezone?.trim();
  if (storedTimezone && storedTimezone !== 'UTC') {
    return storedTimezone;
  }

  const resolvedTimezone =
    listTimezone?.trim() || studioTimezone?.trim() || storedTimezone;
  return resolvedTimezone || undefined;
};

export const formatPrivateLessonSlotTime = (
  slot: { startTime: string; endTime: string },
  timezone?: string | null,
) => formatTrialTimeRange(slot.startTime, slot.endTime, timezone) ?? '-';

export type SeriesRecurrenceFrequency =
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'custom';

const getDayNameFromDateKey = (dateKey: string): string =>
  WEEKDAY_LABELS[new Date(`${dateKey.split('T')[0]}T12:00:00Z`).getUTCDay()];

const addDaysToDateKey = (dateKey: string, days: number): string => {
  const date = new Date(`${dateKey.split('T')[0]}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

const addMonthsToDateKey = (dateKey: string, months: number): string => {
  const date = new Date(`${dateKey.split('T')[0]}T12:00:00`);
  date.setMonth(date.getMonth() + months);
  return toDateKey(date);
};

const findNextDayOfWeekOnOrAfter = (
  fromDateKey: string,
  dayName: string,
): string => {
  let cursor = fromDateKey.split('T')[0];
  for (let i = 0; i < 7; i += 1) {
    if (getDayNameFromDateKey(cursor) === dayName) {
      return cursor;
    }
    cursor = addDaysToDateKey(cursor, 1);
  }
  return cursor;
};

/** Count how many series dates fit repeat + days + range within the offering window. */
export const countSeriesOccurrences = (params: {
  frequency: SeriesRecurrenceFrequency;
  startDate: string;
  endDate: string;
  dayOfWeek?: string;
  daysOfWeek?: string[];
  customDates: string[];
  offering: ClassSearchDetails | null | undefined;
}): number => {
  const allowedDayIndexes = getAllowedDayIndexes(params.offering);
  const windowStart = parseOfferingDate(params.offering?.startDate);
  const windowEnd = parseOfferingDate(params.offering?.endDate);

  const isDateAllowed = (dateKey: string) =>
    isBookablePrivateLessonDate(
      new Date(`${dateKey.split('T')[0]}T12:00:00`),
      params.offering,
      allowedDayIndexes,
      windowStart,
      windowEnd,
    );

  if (params.frequency === 'custom') {
    return params.customDates.filter(isDateAllowed).length;
  }

  const rangeEnd = params.endDate.split('T')[0];
  if (!rangeEnd) {
    return 0;
  }

  const days =
    params.daysOfWeek && params.daysOfWeek.length > 0
      ? params.daysOfWeek
      : params.dayOfWeek
        ? [params.dayOfWeek]
        : [];

  if (days.length === 0) {
    return 0;
  }

  const dates = new Set<string>();
  for (const dayOfWeek of days) {
    let searchFrom = params.startDate.split('T')[0];
    for (let i = 0; i < 500; i += 1) {
      const nextDate = findNextDayOfWeekOnOrAfter(searchFrom, dayOfWeek);
      if (nextDate > rangeEnd) {
        break;
      }
      if (isDateAllowed(nextDate)) {
        dates.add(nextDate);
      }

      if (params.frequency === 'monthly') {
        searchFrom = addMonthsToDateKey(nextDate, 1);
      } else {
        const stepDays = params.frequency === 'biweekly' ? 14 : 7;
        searchFrom = addDaysToDateKey(nextDate, stepDays);
      }

      if (searchFrom > rangeEnd) {
        break;
      }
    }
  }

  return dates.size;
};

export const formatDaysOfWeekLabel = (days: string[]): string => {
  if (days.length === 0) return '';
  if (days.length === 1) return days[0];
  if (days.length === 2) return `${days[0]} & ${days[1]}`;
  return `${days.slice(0, -1).join(', ')}, & ${days[days.length - 1]}`;
};

/**
 * Reasons that describe a settled state of the slot rather than a transient
 * error. Booking the same date again can only produce the same result.
 */
const TERMINAL_BOOKING_FAILURE_PATTERNS = [
  'already booked',
  'slot is full',
  'outside the offering booking window',
  'available offering days',
  'in the past',
  'end time must be after start time',
  'invalid private lesson slot',
];

export const isRetryableBookingFailure = (reason: string): boolean => {
  const normalized = reason.toLowerCase();
  return !TERMINAL_BOOKING_FAILURE_PATTERNS.some((pattern) =>
    normalized.includes(pattern),
  );
};
